import { useState, useEffect } from "react";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import Icon1 from "@assets/icons/Icon";
import { useDataStore } from "@store/user_org";
import { organizationsApi } from "@services/api/organizations";
import { paymentsApi } from "@services/api/payments";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import type { Payment, Facture } from "types/payment";
import { PaymentsChart, PaymentSuccessRateChart } from "@components/charts/PaymentsChart";

const STATE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: "Brouillon", color: "#6B7280", bg: "#F3F4F6" },
  initiated: { label: "Initié", color: "#2563EB", bg: "#DBEAFE" },
  pending: { label: "En cours", color: "#D97706", bg: "#FEF3C7" },
  paid: { label: "Payé", color: "#059669", bg: "#D1FAE5" },
  error: { label: "Erreur", color: "#DC2626", bg: "#FEE2E2" },
  cancelled: { label: "Annulé", color: "#6B7280", bg: "#F3F4F6" },
};

export function Payments() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);

  const bridge = orgData?.bridge;

  const [iban, setIban] = useState(bridge?.iban || "");
  const [companyName, setCompanyName] = useState(bridge?.company_name || "");
  const [firstName, setFirstName] = useState(bridge?.first_name || "");
  const [lastName, setLastName] = useState(bridge?.last_name || "");
  const [email, setEmail] = useState(bridge?.email || "");
  const [saving, setSaving] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [factures, setFactures] = useState<Facture[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingFactures, setLoadingFactures] = useState(false);
  const [showConfig, setShowConfig] = useState(!bridge?.connected);

  useEffect(() => {
    if (bridge) {
      setIban(bridge.iban || "");
      setCompanyName(bridge.company_name || "");
      setFirstName(bridge.first_name || "");
      setLastName(bridge.last_name || "");
      setEmail(bridge.email || "");
    }
  }, [bridge]);

  useEffect(() => {
    setLoadingPayments(true);
    paymentsApi.listAll().then(setPayments).catch((e) => console.error("Failed to load payments:", e)).finally(() => setLoadingPayments(false));
    setLoadingFactures(true);
    paymentsApi.listFactures().then(setFactures).catch((e) => console.error("Failed to load factures:", e)).finally(() => setLoadingFactures(false));
  }, []);

  const handleSave = async () => {
    if (!orgData) return;
    setSaving(true);
    try {
      await organizationsApi.update(orgData.id, {
        bridge: {
          iban,
          company_name: companyName,
          first_name: firstName,
          last_name: lastName,
          email,
          connected: !!iban,
        },
      } as any);
      await fetchOrgData(orgData.id);
      showNotification({ type: "success", message: "Configuration Bridge enregistrée" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const configForm = (
    <div
      className="flex flex-col gap-4 p-6 rounded-xl"
      style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
    >
      <TextField
        label="IBAN"
        value={iban.replace(/\s/g, "").replace(/(.{4})/g, "$1 ").trim()}
        onChange={(v: string) => setIban(v.replace(/\s/g, ""))}
      />
      <TextField
        label="Nom de l'entreprise"
        value={companyName}
        onChange={setCompanyName}
      />
      <div className="flex flex-row gap-3">
        <div className="flex-1">
          <TextField label="Prénom" value={firstName} onChange={setFirstName} />
        </div>
        <div className="flex-1">
          <TextField label="Nom" value={lastName} onChange={setLastName} />
        </div>
      </div>
      <TextField label="Email" value={email} onChange={setEmail} />
      {bridge?.fees != null && (
        <div className="flex flex-row items-center gap-2">
          <span
            className="text-h3 font-medium"
            style={{ fontFamily: "Poppins", color: "#6B7280" }}
          >
            Frais de transaction :
          </span>
          <span
            className="text-h2 font-semibold"
            style={{ fontFamily: "Poppins", color: "#111827" }}
          >
            {bridge.fees}%
          </span>
        </div>
      )}
      <div className="flex flex-row justify-end mt-2">
        <Button
          label={saving ? "Enregistrement..." : "Enregistrer"}
          onClick={handleSave}
          disabled={saving}
        />
      </div>
    </div>
  );

  const paymentsList = (
    <div>
      <h2 className="text-h1 font-semibold mb-4" style={{ fontFamily: "Poppins" }}>
        Historique des paiements
      </h2>
      {loadingPayments ? (
        <p className="text-h3 text-neutral-400" style={{ fontFamily: "Poppins" }}>Chargement...</p>
      ) : payments.length === 0 ? (
        <p className="text-h3 text-neutral-400" style={{ fontFamily: "Poppins" }}>Aucun paiement pour le moment.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {payments.map((p) => {
            const s = STATE_LABELS[p.state] || STATE_LABELS.draft;
            return (
              <div
                key={p.id}
                className="flex flex-row items-center justify-between p-4 rounded-xl"
                style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
              >
                <div className="flex flex-col gap-0.5">
                  <div className="flex flex-row items-center gap-2">
                    <span className="text-h2 font-semibold" style={{ fontFamily: "Poppins", color: "#111827" }}>
                      {p.amount.toFixed(2)} €
                    </span>
                    <span
                      className="text-h4 font-medium px-2 py-0.5 rounded-full"
                      style={{ fontFamily: "Poppins", color: s.color, background: s.bg }}
                    >
                      {s.label}
                    </span>
                    <span
                      className="text-h4 font-medium px-2 py-0.5 rounded-full"
                      style={{ fontFamily: "Poppins", color: "#6366F1", background: "#EEF2FF" }}
                    >
                      {p.type === "acompte" ? "Acompte" : "Solde"}
                    </span>
                  </div>
                  <span className="text-h4" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
                    {p.reglement_infos.name}
                    {p.reglement_infos.first_name ? ` ${p.reglement_infos.first_name}` : ""}
                    {" — "}
                    {p.reglement_infos.email}
                  </span>
                </div>
                <span className="text-h4" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>
                  {formatDate(p.created_at)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const facturesList = (
    <div>
      <h2 className="text-h1 font-semibold mb-4" style={{ fontFamily: "Poppins" }}>
        Factures
      </h2>
      {loadingFactures ? (
        <p className="text-h3 text-neutral-400" style={{ fontFamily: "Poppins" }}>Chargement...</p>
      ) : factures.length === 0 ? (
        <p className="text-h3 text-neutral-400" style={{ fontFamily: "Poppins" }}>Aucune facture pour le moment.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {factures.map((f) => (
            <div
              key={f.id}
              className="flex flex-row items-center justify-between p-4 rounded-xl hover:bg-neutral-50 transition-colors cursor-pointer"
              style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
              onClick={() => navigateTo({ route: `/ordres/${f.ordre_id}` })}
            >
              <div className="flex flex-row items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "#EEF2FF" }}
                >
                  <Icon1 name="facture" size={18} color="#6366F1" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-h2 font-semibold" style={{ fontFamily: "Poppins", color: "#111827" }}>
                    {f.name}
                  </span>
                  <span className="text-h4" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
                    Ordre #{f.ordre_num || "—"} — {f.ordre_name || "—"}
                    {f.amount != null ? ` — ${f.amount.toFixed(2)} €` : ""}
                  </span>
                </div>
              </div>
              <div className="flex flex-row items-center gap-3">
                <span className="text-h4" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>
                  {formatDate(f.created_at)}
                </span>
                {f.url && (
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-neutral-200 transition-colors"
                    title="Telecharger"
                  >
                    <Icon1 name="download" size={16} color="#6B7280" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const howItWorks = (
    <div
      className="flex flex-col gap-4 p-5 rounded-lg"
      style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
    >
      <span className="text-h2 font-semibold" style={{ fontFamily: "Poppins", color: "#374151" }}>
        Comment ça marche
      </span>
      <div className="flex flex-col gap-3">
        {[
          { step: "1", title: "Configuration", desc: "Renseignez l'IBAN de votre compte professionnel et vos coordonnées." },
          { step: "2", title: "Demande de paiement", desc: "Depuis un ordre de réparation, demandez un acompte ou le solde. Le client reçoit un lien de paiement par email." },
          { step: "3", title: "Paiement sécurisé", desc: "Le client effectue un virement bancaire instantané via Bridge (Bankin'). Aucune carte bancaire nécessaire." },
          { step: "4", title: "Confirmation", desc: "Vous êtes notifié dès que le paiement est reçu. Le suivi client est mis à jour automatiquement." },
        ].map((item) => (
          <div key={item.step} className="flex flex-row gap-3">
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{ width: 28, height: 28, borderRadius: 8, background: "#3B82F6", color: "#FFFFFF", fontFamily: "Poppins", fontWeight: 700, fontSize: 13 }}
            >
              {item.step}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-h3 font-semibold" style={{ fontFamily: "Poppins", color: "#374151" }}>{item.title}</span>
              <span className="text-h4" style={{ fontFamily: "Poppins", color: "#6B7280" }}>{item.desc}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col p-8 max-w-2xl">
      <h1 className="text-hx font-semibold mb-2" style={{ fontFamily: "Poppins" }}>
        Paiements en ligne
      </h1>

      {bridge?.connected ? (
        <>
          {/* Status badge */}
          <div
            className="flex flex-row items-center gap-2 mb-6 p-3 rounded-lg"
            style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}
          >
            <div style={{ width: 10, height: 10, borderRadius: 999, background: "#22C55E" }} />
            <span className="text-h2 font-medium" style={{ fontFamily: "Poppins", color: "#166534" }}>
              Connecté — prêt à recevoir des paiements
            </span>
          </div>

          {/* Charts */}
          {payments.length > 0 && (
            <div className="flex flex-col gap-4 mb-8">
              <div className="p-5 rounded-xl" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
                <PaymentsChart payments={payments} showPeriodSelector defaultPeriod="12m" />
              </div>
              <div className="p-5 rounded-xl" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
                <PaymentSuccessRateChart payments={payments} />
              </div>
            </div>
          )}

          {/* Payments history */}
          <div className="mb-8">{paymentsList}</div>

          {/* Factures */}
          <div className="mb-8">{facturesList}</div>

          {/* Config — collapsed by default */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => setShowConfig((v) => !v)}
              className="flex flex-row items-center gap-2 text-h3 font-medium text-neutral-400 hover:text-neutral-600 transition-colors w-fit"
              style={{ fontFamily: "Poppins" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 transition-transform"
                style={{ transform: showConfig ? "rotate(90deg)" : "rotate(0deg)" }}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Modifier la configuration
            </button>
            {showConfig && configForm}
          </div>
        </>
      ) : (
        <>
          <p className="text-h2 text-neutral-500 mb-8" style={{ fontFamily: "Poppins" }}>
            Configurez vos informations bancaires pour recevoir des paiements par virement via Bridge. Vos clients recevront un lien de paiement sécurisé par email.
          </p>

          {/* Status badge */}
          <div
            className="flex flex-row items-center gap-2 mb-6 p-3 rounded-lg"
            style={{ background: "#FEF9C3", border: "1px solid #FDE68A" }}
          >
            <div style={{ width: 10, height: 10, borderRadius: 999, background: "#F59E0B" }} />
            <span className="text-h2 font-medium" style={{ fontFamily: "Poppins", color: "#92400E" }}>
              Non configuré — renseignez vos informations ci-dessous
            </span>
          </div>

          <div className="flex flex-col gap-8">
            {howItWorks}
            {configForm}
            {factures.length > 0 && facturesList}
          </div>
        </>
      )}
    </div>
  );
}
